import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Dialog,
  Button,
  Field,
  Input,
  Textarea,
  NativeSelect,
  VStack,
  HStack,
  Box,
  Text,
  Avatar,
  Badge,
  Checkbox,
} from '@chakra-ui/react';
import { tasksAPI, projectsAPI } from '../services/api';
import { Task, User, Column, Comment } from '../types';
import { toast } from '../utils/toast';
import { useAuth } from '../context/AuthContext';
import { CommentRichEditor } from './CommentRichEditor';
import { sanitizeHtml, isEmptyHtml } from '../utils/sanitizeHtml';
import '../styles/commentContent.css';

interface TaskModalProps {
  open: boolean;
  onClose: () => void;
  projectId: string;
  task?: Task | null;
  onSaved: () => void;
}

const TaskModal: React.FC<TaskModalProps> = ({ open, onClose, projectId, task, onSaved }) => {
  const { user: currentUser } = useAuth();
  const [fullTask, setFullTask] = useState<Task | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<string>('todo');
  const [priority, setPriority] = useState<'low' | 'medium' | 'high'>('medium');
  const [assignedTo, setAssignedTo] = useState('');
  const [members, setMembers] = useState<User[]>([]);
  const [columns, setColumns] = useState<Column[]>([]);
  const [loading, setLoading] = useState(false);
  const [comments, setComments] = useState<Comment[]>([]);
  const [newComment, setNewComment] = useState('');
  const [commentLoading, setCommentLoading] = useState(false);
  const [dependsOn, setDependsOn] = useState<string[]>([]);
  const [participants, setParticipants] = useState<string[]>([]);
  const [projectTasks, setProjectTasks] = useState<Task[]>([]);
  const [finishedColumnId, setFinishedColumnId] = useState<string | null>(null);
  const saveTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastSavedRef = useRef<{ title: string; description: string; status: string; priority: string; assignedTo: string; dependsOn: string[]; participants: string[] } | null>(null);

  const loadProjectTasks = useCallback(async () => {
    try {
      const response = await tasksAPI.getByProject(projectId);
      setProjectTasks(response.data);
    } catch (error) {
      console.error('Failed to load project tasks', error);
    }
  }, [projectId]);

  useEffect(() => {
    if (open) {
      loadProjectMembers();
      loadProjectTasks();
      if (task) {
        loadFullTask();
      } else {
        lastSavedRef.current = null;
        setTitle('');
        setDescription('');
        setPriority('medium');
        setAssignedTo('');
        setDependsOn([]);
        setFullTask(null);
        setComments([]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, task, loadProjectTasks]);

  const toUserId = (u: any) => String((u?._id ?? u?.id) ?? '').trim() || null;
  const normalizeParticipantIds = (participantsArray: any[], assigned?: any): string[] => {
    const ids: string[] = [];
    if (Array.isArray(participantsArray) && participantsArray.length > 0) {
      participantsArray.forEach((p) => {
        const id = toUserId(p);
        if (id && !ids.includes(id)) ids.push(id);
      });
    }
    if (assigned) {
      const aId = toUserId(assigned);
      if (aId && !ids.includes(aId)) ids.unshift(aId);
    }
    return ids;
  };

  const loadFullTask = async () => {
    if (!task?._id) return;
    try {
      const response = await tasksAPI.getById(task._id);
      const loadedTask = response.data;
      setFullTask(loadedTask);
      setTitle(loadedTask.title);
      setDescription(loadedTask.description || '');
      setStatus(loadedTask.status);
      setPriority(loadedTask.priority);
      const assignedIds = normalizeParticipantIds(loadedTask.participants || [], loadedTask.assignedTo);
      setParticipants(assignedIds);
      setAssignedTo(assignedIds[0] || '');
      setComments(loadedTask.comments || []);
      const depIds = (loadedTask.dependsOn || []).map((d: any) => d._id || d);
      setDependsOn(depIds);
      lastSavedRef.current = {
        title: loadedTask.title,
        description: loadedTask.description || '',
        status: loadedTask.status,
        priority: loadedTask.priority,
        assignedTo: assignedIds[0] || '',
        dependsOn: depIds,
        participants: assignedIds,
      };
    } catch (error) {
      console.error('Failed to load task details', error);
      // Fallback to provided task data
      if (task) {
        setTitle(task.title);
        setDescription(task.description || '');
        setStatus(task.status);
        setPriority(task.priority);
        const assignedIds = normalizeParticipantIds((task as any).participants || [], task.assignedTo);
        setParticipants(assignedIds);
        setAssignedTo(assignedIds[0] || '');
        setComments(task.comments || []);
        const depIds = (task.dependsOn || []).map((d: any) => d._id || d);
        setDependsOn(depIds);
        lastSavedRef.current = {
          title: task.title,
          description: task.description || '',
          status: task.status,
          priority: task.priority,
          assignedTo: assignedIds[0] || '',
          dependsOn: depIds,
          participants: assignedIds,
        };
      }
    }
  };

  const loadProjectMembers = async () => {
    try {
      const response = await projectsAPI.getById(projectId);
      setMembers(response.data.members || []);
      setFinishedColumnId(response.data.finishedColumnId ?? null);
      if (response.data.columns && response.data.columns.length > 0) {
        setColumns(response.data.columns.sort((a: Column, b: Column) => a.order - b.order));
        // Set default status to first column if creating new task
        if (!task && response.data.columns.length > 0) {
          setStatus(response.data.columns[0].id);
        }
      } else {
        // Fallback to default columns
        const defaultColumns: Column[] = [
          { id: 'todo', title: 'To Do', color: '#9CA3AF', order: 0 },
          { id: 'in-progress', title: 'In Progress', color: '#3B82F6', order: 1 },
          { id: 'done', title: 'Done', color: '#10B981', order: 2 }
        ];
        setColumns(defaultColumns);
        if (!task) {
          setStatus('todo');
        }
      }
    } catch (error) {
      console.error('Failed to load project members', error);
    }
  };

  const saveTask = useCallback(async (taskData: { title: string; description: string; status: string; priority: string; assignedTo: string; dependsOn: string[]; participants: string[] }) => {
    if (!task?._id) return;
    try {
      await tasksAPI.update(task._id, {
        ...taskData,
        project: projectId,
        assignedTo: taskData.assignedTo || undefined,
        participants: taskData.participants || [],
        dependsOn: taskData.dependsOn || [],
      });
      lastSavedRef.current = taskData;
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save task',
        status: 'error',
      });
    }
  }, [task?._id, projectId, onSaved]);

  const currentTaskForSave = fullTask || task;
  const canEditForSave = !task ? true : !!(currentUser?.role === 'admin' || currentUser?.role === 'masterAdmin' || currentUser?.email === 'admin@admin.com' || (currentUser?.id && ((currentTaskForSave?.assignedTo as any)?._id || (currentTaskForSave?.assignedTo as any)?.id)?.toString() === currentUser.id));

  useEffect(() => {
    if (!task?._id || !title.trim()) return;
    if (!lastSavedRef.current) return;
    if (!canEditForSave) return;
    const current = { title, description, status, priority, assignedTo, dependsOn, participants };
    const last = lastSavedRef.current;
    const depEqual = JSON.stringify([...(last.dependsOn || [])].sort()) === JSON.stringify([...dependsOn].sort());
    if (last && last.title === current.title && last.description === current.description &&
        last.status === current.status && last.priority === current.priority && last.assignedTo === current.assignedTo && depEqual &&
        JSON.stringify([...(last.participants || [])].sort()) === JSON.stringify([...participants].sort())) {
      return;
    }
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      saveTask(current);
    }, 600);
    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
        saveTimeoutRef.current = null;
      }
    };
  }, [task?._id, title, description, status, priority, assignedTo, dependsOn, participants, saveTask, canEditForSave]);

  const handleSubmit = async () => {
    if (!title.trim()) {
      toast({
        title: 'Error',
        description: 'Title is required',
        status: 'error',
      });
      return;
    }

    setLoading(true);
    try {
      const uniqueParticipants = Array.from(new Set(participants.filter(Boolean)));
      const taskData = {
        title,
        description,
        status,
        priority,
        project: projectId,
        assignedTo: (assignedTo || uniqueParticipants[0]) || undefined,
        participants: uniqueParticipants,
      };

      if (task) {
        await tasksAPI.update(task._id, { ...taskData, dependsOn });
        lastSavedRef.current = { title, description, status, priority, assignedTo: taskData.assignedTo || '', dependsOn, participants: uniqueParticipants };
        onSaved();
      } else {
        await tasksAPI.create({ ...taskData, dependsOn });
        toast({ title: 'Success', description: 'Task created', status: 'success' });
        onSaved();
        onClose();
      }
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to save task',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    
    setLoading(true);
    try {
      await tasksAPI.delete(task._id);
      toast({
        title: 'Success',
        description: 'Task deleted',
        status: 'success',
      });
      onSaved();
      onClose();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: 'Failed to delete task',
        status: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async () => {
    if (!task?._id || isEmptyHtml(newComment)) return;

    setCommentLoading(true);
    try {
      const response = await tasksAPI.addComment(task._id, newComment);
      setComments(response.data.comments || []);
      setNewComment('');
      toast({
        title: 'Success',
        description: 'Comment added',
        status: 'success',
      });
      // Reload full task to get updated data
      await loadFullTask();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to add comment',
        status: 'error',
      });
    } finally {
      setCommentLoading(false);
    }
  };

  const handleDeleteComment = async (commentId: string) => {
    if (!task?._id) return;

    setCommentLoading(true);
    try {
      const response = await tasksAPI.deleteComment(task._id, commentId);
      setComments(response.data.comments || []);
      toast({
        title: 'Success',
        description: 'Comment deleted',
        status: 'success',
      });
      // Reload full task to get updated data
      await loadFullTask();
    } catch (error: any) {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to delete comment',
        status: 'error',
      });
    } finally {
      setCommentLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const currentTask = fullTask || task;
  const isAdmin = currentUser?.role === 'admin';
  const isMasterAdmin = currentUser?.role === 'masterAdmin' || currentUser?.email === 'admin@admin.com';
  const creatorIdForTask = (currentTask?.createdBy as any)?._id || (currentTask?.createdBy as any)?.id;
  const isCreatorOfTask =
    !!currentUser?.id && !!creatorIdForTask && creatorIdForTask.toString() === currentUser.id;
  const participantIdsForEdit: string[] = [];
  if (Array.isArray(currentTask?.participants)) {
    participantIdsForEdit.push(
      ...(currentTask!.participants as any[])
        .map((p: any) => (p?._id || p?.id)?.toString?.())
        .filter(Boolean) as string[]
    );
  }
  if (currentTask?.assignedTo) {
    const aId = ((currentTask.assignedTo as any)?._id || (currentTask.assignedTo as any)?.id)?.toString?.();
    if (aId) participantIdsForEdit.push(aId);
  }
  const canEdit = !task
    ? true
    : !!(
        isAdmin ||
        isMasterAdmin ||
        (currentUser?.id && participantIdsForEdit.includes(currentUser.id))
      );

  // Only admins, master admins or task creator can manage participants list
  const canEditParticipants = !task ? true : !!(isAdmin || isMasterAdmin || isCreatorOfTask);

  const isFullScreenLayout = !!task;

  return (
    <Dialog.Root open={open} onOpenChange={(e: { open: boolean }) => !e.open && onClose()} size="full">
      <Dialog.Backdrop />
      <Dialog.Positioner>
        <Dialog.Content
          maxW="100vw"
          maxH="100vh"
          w="100vw"
          h="100vh"
          borderRadius={0}
          overflow="hidden"
          display="flex"
          flexDirection="column"
        >
          <Dialog.Header flexShrink={0} borderBottomWidth={1} borderColor="gray.200">
            <Dialog.Title>{task ? 'Task details' : 'New task'}</Dialog.Title>
            {task && !canEdit && (
              <Badge colorScheme="gray" ml={3} fontSize="xs">View only</Badge>
            )}
            <Dialog.CloseTrigger position="absolute" top={4} right={4} />
          </Dialog.Header>

          {isFullScreenLayout ? (
            <Box flex={1} display="flex" flexDirection="row" minH={0} overflow="hidden">
              {/* Leva kolona: naziv, opis, status, itd. */}
              <Box
                flex="1"
                display="flex"
                flexDirection="column"
                borderRightWidth={1}
                borderColor="gray.200"
                minW={0}
              >
                <Box flex={1} overflowY="auto" p={6}>
                  <VStack gap={5} align="stretch">
                  <Field.Root required>
                    <Field.Label>Name</Field.Label>
                    <Input
                      value={title}
                      onChange={(e) => canEdit && setTitle(e.target.value)}
                      placeholder="Task name"
                      size="lg"
                      fontWeight="semibold"
                      readOnly={!canEdit}
                    />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Description</Field.Label>
                    <Textarea
                      value={description}
                      onChange={(e) => canEdit && setDescription(e.target.value)}
                      placeholder="Task description"
                      rows={6}
                      readOnly={!canEdit}
                    />
                  </Field.Root>
                  <HStack gap={4} flexWrap="wrap">
                    <Field.Root flex="1" minW="160px">
                      <Field.Label fontWeight="medium">Status</Field.Label>
                      <NativeSelect.Root disabled={!canEdit}>
                        <NativeSelect.Field value={status} onChange={(e: any) => canEdit && setStatus(e.target.value)} mt={1} borderRadius="lg">
                          {columns.map((column) => (
                            <option key={column.id} value={column.id}>{column.title}</option>
                          ))}
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    </Field.Root>
                    <Field.Root flex="1" minW="160px">
                      <Field.Label fontWeight="medium">Priority</Field.Label>
                      <NativeSelect.Root disabled={!canEdit}>
                        <NativeSelect.Field value={priority} onChange={(e: any) => canEdit && setPriority(e.target.value as any)} mt={1} borderRadius="lg">
                          <option value="low">Low</option>
                          <option value="medium">Medium</option>
                          <option value="high">High</option>
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    </Field.Root>
                  </HStack>
                  <HStack gap={6} flexWrap="wrap" alignItems="flex-start">
                    <Field.Root flex={1} minW="240px">
                      <Field.Label fontWeight="medium">Assigned (multiple users)</Field.Label>
                      <Box
                        mt={1}
                        w="100%"
                        borderWidth={1}
                        borderRadius="lg"
                        borderColor="gray.200"
                        maxH="210px"
                        overflowY="auto"
                        bg="gray.50"
                        _dark={{ bg: 'gray.800', borderColor: 'gray.600' }}
                      >
                        <VStack align="stretch" gap={0} p={1}>
                          {members.length === 0 && (
                            <Text fontSize="sm" color="gray.500" p={4} textAlign="center">
                              No project members.
                            </Text>
                          )}
                          {members.map((member) => {
                            const id = String((member as any)._id ?? (member as any).id ?? '');
                            if (!id) return null;
                            const checked = participants.includes(id);
                            const handleToggle = () => {
                              if (!canEditParticipants) return;
                              const next = checked
                                ? participants.filter((x) => x !== id)
                                : [...participants, id];
                              setParticipants(next);
                              setAssignedTo(next[0] || '');
                            };
                            return (
                              <Box
                                key={id}
                                px={3}
                                py={2}
                                borderRadius="md"
                                cursor={canEditParticipants ? 'pointer' : 'default'}
                                bg={checked ? 'purple.100' : 'transparent'}
                                _hover={canEditParticipants ? { bg: checked ? 'purple.200' : 'gray.100' } : undefined}
                                transition="background 0.15s"
                                onClick={handleToggle}
                              >
                                <Checkbox.Root checked={checked} cursor={canEditParticipants ? 'pointer' : 'default'} pointerEvents="none">
                                  <Checkbox.HiddenInput />
                                  <Checkbox.Control />
                                  <Checkbox.Label ml={2} fontWeight={checked ? 'medium' : 'normal'}>
                                    {member.name}
                                    {checked && participants[0] === id && (
                                      <Badge ml={2} size="sm" colorScheme="purple" variant="subtle">
                                        Main
                                      </Badge>
                                    )}
                                  </Checkbox.Label>
                                </Checkbox.Root>
                              </Box>
                            );
                          })}
                        </VStack>
                      </Box>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        The first selected will be the main assignee. Click a row to select.
                      </Text>
                    </Field.Root>

                    <Field.Root flex={1} minW="240px">
                      <Field.Label fontWeight="medium">Depends on</Field.Label>
                      <NativeSelect.Root disabled={!canEdit}>
                        <NativeSelect.Field
                          value=""
                          mt={1}
                          borderRadius="lg"
                          onChange={(e: any) => {
                            if (!canEdit) return;
                            const id = e.target.value;
                            if (id && !dependsOn.includes(id)) setDependsOn([...dependsOn, id]);
                          }}
                        >
                          <option value="">Add task...</option>
                          {projectTasks
                            .filter(
                              (t) =>
                                t._id !== task?._id &&
                                !dependsOn.includes(t._id) &&
                                t.status !== finishedColumnId
                            )
                            .map((t) => (
                              <option key={t._id} value={t._id}>
                                {t.title}
                              </option>
                            ))}
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                      <Text fontSize="xs" color="gray.500" mt={1}>
                        Tasks you must complete before this one.
                      </Text>
                    </Field.Root>
                  </HStack>
                  {dependsOn.length > 0 && (
                    <Box mt={3} p={4} borderRadius="lg" borderWidth={1} borderColor="gray.200" bg="gray.50">
                      <VStack gap={2} align="stretch">
                        {dependsOn.map((id) => {
                          const t =
                            projectTasks.find((x) => x._id === id) ||
                            fullTask?.dependsOn?.find((d: any) => (d._id || d) === id);
                          const statusCol = t?.status === 'done' ? 'green' : 'yellow';
                          return (
                            <HStack key={id} justifyContent="space-between" fontSize="sm">
                              <HStack gap={2}>
                                <Badge colorScheme={statusCol} size="sm" variant="subtle">
                                  {t?.title || id.slice(-6)}
                                </Badge>
                              </HStack>
                              {canEdit && (
                                <Button
                                  size="xs"
                                  variant="ghost"
                                  colorScheme="gray"
                                  onClick={() => setDependsOn(dependsOn.filter((d) => d !== id))}
                                  aria-label="Ukloni"
                                >
                                  ×
                                </Button>
                              )}
                            </HStack>
                          );
                        })}
                      </VStack>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        This task can only be done after the listed tasks are completed.
                      </Text>
                    </Box>
                  )}

                  {currentTask && (
                    <Box pt={4} borderTopWidth={1} borderColor="gray.200">
                      <Text fontSize="sm" fontWeight="medium" color="gray.600" mb={3}>Information</Text>
                      <VStack gap={2} align="stretch">
                        <HStack justifyContent="space-between">
                          <Text fontSize="sm" color="gray.600">Created by:</Text>
                          <Text fontSize="sm" fontWeight="medium">{currentTask.createdBy?.name || '—'}</Text>
                        </HStack>
                        <HStack justifyContent="space-between">
                          <Text fontSize="sm" color="gray.600">Created:</Text>
                          <Text fontSize="sm">{formatDate(currentTask.createdAt)}</Text>
                        </HStack>
                        {currentTask.updatedAt && currentTask.updatedAt !== currentTask.createdAt && (
                          <HStack justifyContent="space-between">
                            <Text fontSize="sm" color="gray.600">Modified:</Text>
                            <Text fontSize="sm">{formatDate(currentTask.updatedAt)}</Text>
                          </HStack>
                        )}
                      </VStack>
                    </Box>
                  )}
                  </VStack>
                </Box>

                <HStack gap={3} p={6} pt={4} justifyContent="flex-end" flexShrink={0} borderTopWidth={1} borderColor="gray.100">
                  {task && canEdit && (
                    <Button colorScheme="red" variant="outline" onClick={handleDelete} loading={loading} size="sm">
                      Delete task
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => {
                      onSaved();
                      onClose();
                    }}
                    size="sm"
                  >
                    Close
                  </Button>
                  {!task && (
                    <Button colorScheme="blue" onClick={handleSubmit} loading={loading} size="sm">
                      Create
                    </Button>
                  )}
                </HStack>
              </Box>

              {/* Desna kolona: komentari */}
              <Box flex="1" display="flex" flexDirection="column" minW={0} bg="gray.50">
                <Box px={6} py={4} borderBottomWidth={1} borderColor="gray.200" flexShrink={0}>
                  <Text fontSize="lg" fontWeight="semibold">Comments ({comments.length})</Text>
                </Box>
                <Box flex={1} overflowY="auto" p={6}>
                  <VStack gap={4} align="stretch">
                    {comments.length === 0 ? (
                      <Text fontSize="sm" color="gray.500" textAlign="center" py={8}>
                        No comments. Be the first to comment.
                      </Text>
                    ) : (
                      comments.map((comment) => (
                        <Box
                          key={comment._id}
                          p={4}
                          bg="white"
                          borderRadius="md"
                          borderWidth={1}
                          borderColor="gray.200"
                          boxShadow="sm"
                        >
                          <HStack justifyContent="space-between" mb={2}>
                            <HStack gap={2}>
                              <Avatar.Root size="sm">
                                <Avatar.Fallback bg="gray.200" color="black" fontWeight="bold">
                                  {comment.createdBy?.name?.charAt(0).toUpperCase() || 'U'}
                                </Avatar.Fallback>
                              </Avatar.Root>
                              <Box>
                                <Text fontSize="sm" fontWeight="medium">{comment.createdBy?.name || 'Unknown'}</Text>
                                <Text fontSize="xs" color="gray.500">{formatDate(comment.createdAt)}</Text>
                              </Box>
                            </HStack>
                            {canEdit && currentUser && (comment.createdBy?.id === currentUser.id || (currentTask?.createdBy as any)?.id === currentUser.id) && (
                              <Button size="xs" variant="ghost" colorScheme="red" onClick={() => handleDeleteComment(comment._id)} disabled={commentLoading}>
                                Delete
                              </Button>
                            )}
                          </HStack>
                          {comment.text && comment.text.includes('<') ? (
                            <div
                              className="comment-html-content"
                              style={{ fontSize: '14px', color: '#374151', whiteSpace: 'pre-wrap' }}
                              dangerouslySetInnerHTML={{ __html: sanitizeHtml(comment.text) }}
                            />
                          ) : (
                            <Text fontSize="sm" color="gray.700" whiteSpace="pre-wrap">
                              {comment.text}
                            </Text>
                          )}
                        </Box>
                      ))
                    )}
                  </VStack>
                </Box>
                {canEdit && (
                <Box p={6} borderTopWidth={1} borderColor="gray.200" flexShrink={0} bg="white" width="100%">
                  <VStack gap={3} align="stretch" width="100%">
                    <Field.Root width="100%">
                      <Field.Label>Add comment (Ctrl+B bold, Ctrl+I italic, Ctrl+U underline)</Field.Label>
                      <CommentRichEditor
                        value={newComment}
                        onChange={setNewComment}
                        placeholder="Write a comment..."
                        minHeight="100px"
                        disabled={commentLoading}
                      />
                    </Field.Root>
                    <Button colorScheme="blue" onClick={handleAddComment} loading={commentLoading} disabled={isEmptyHtml(newComment)} size="sm" alignSelf="flex-start">
                      Add comment
                    </Button>
                  </VStack>
                </Box>
                )}
              </Box>
            </Box>
          ) : (
            <Box flex={1} display="flex" flexDirection="column" minH={0}>
              <Dialog.Body flex={1} overflowY="auto" minH={0}>
                <VStack gap={4} align="stretch">
                  <Field.Root required>
                    <Field.Label>Name</Field.Label>
                    <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Task name" />
                  </Field.Root>
                  <Field.Root>
                    <Field.Label>Description</Field.Label>
                    <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" rows={4} />
                  </Field.Root>
                  <HStack gap={4}>
                    <Field.Root flex={1}>
                      <Field.Label>Status</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field value={status} onChange={(e: any) => setStatus(e.target.value)}>
                          {columns.map((col) => (<option key={col.id} value={col.id}>{col.title}</option>))}
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    </Field.Root>
                    <Field.Root flex={1}>
                      <Field.Label>Priority</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field value={priority} onChange={(e: any) => setPriority(e.target.value as any)}>
                          <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    </Field.Root>
                  </HStack>
                  <HStack gap={6} flexWrap="wrap" alignItems="flex-start">
                    <Field.Root flex={1} minW="240px">
                      <Field.Label fontWeight="medium">Assigned (multiple users)</Field.Label>
                      <Box
                        mt={1}
                        w="100%"
                        borderWidth={1}
                        borderRadius="lg"
                        borderColor="gray.200"
                        maxH="210px"
                        overflowY="auto"
                        bg="gray.50"
                        p={1}
                      >
                        <VStack align="stretch" gap={0}>
                          {members.length === 0 && (
                            <Text fontSize="sm" color="gray.500" p={4} textAlign="center">
                              No project members.
                            </Text>
                          )}
                          {members.map((m) => {
                            const id = String((m as any)._id ?? (m as any).id ?? '');
                            if (!id) return null;
                            const checked = participants.includes(id);
                            const handleToggle = () => {
                              const next = checked
                                ? participants.filter((x) => x !== id)
                                : [...participants, id];
                              setParticipants(next);
                              setAssignedTo(next[0] || '');
                            };
                            return (
                              <Box
                                key={id}
                                px={3}
                                py={2}
                                borderRadius="md"
                                cursor={canEditParticipants ? 'pointer' : 'default'}
                                bg={checked ? 'purple.100' : 'transparent'}
                                _hover={canEditParticipants ? { bg: checked ? 'purple.200' : 'gray.100' } : undefined}
                                transition="background 0.15s"
                                onClick={canEditParticipants ? handleToggle : undefined}
                              >
                                <Checkbox.Root checked={checked} cursor={canEditParticipants ? 'pointer' : 'default'} pointerEvents="none">
                                  <Checkbox.HiddenInput />
                                  <Checkbox.Control />
                                  <Checkbox.Label ml={2} fontWeight={checked ? 'medium' : 'normal'}>
                                    {m.name}
                                    {checked && participants[0] === id && (
                                      <Badge ml={2} size="sm" colorScheme="purple" variant="subtle">
                                        Main
                                      </Badge>
                                    )}
                                  </Checkbox.Label>
                                </Checkbox.Root>
                              </Box>
                            );
                          })}
                        </VStack>
                      </Box>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        The first selected will be the main assignee. Click a row to select.
                      </Text>
                    </Field.Root>
                    <Field.Root flex={1} minW="240px">
                      <Field.Label>Depends on</Field.Label>
                      <NativeSelect.Root>
                        <NativeSelect.Field
                          value=""
                          onChange={(e: any) => {
                            const id = e.target.value;
                            if (id && !dependsOn.includes(id)) setDependsOn([...dependsOn, id]);
                          }}
                        >
                          <option value="">Add task...</option>
                          {projectTasks
                            .filter(
                              (t) =>
                                !dependsOn.includes(t._id) &&
                                t.status !== finishedColumnId
                            )
                            .map((t) => (
                              <option key={t._id} value={t._id}>
                                {t.title}
                              </option>
                            ))}
                        </NativeSelect.Field>
                      </NativeSelect.Root>
                    </Field.Root>
                  </HStack>
                  {dependsOn.length > 0 && (
                    <Box mt={2} p={3} borderRadius="md" borderWidth={1} borderColor="gray.200" bg="gray.50">
                      <VStack gap={2} align="stretch">
                        {dependsOn.map((id) => {
                          const t = projectTasks.find((x) => x._id === id);
                          const statusCol = t?.status === 'done' ? 'green' : 'yellow';
                          return (
                            <HStack key={id} justifyContent="space-between" fontSize="sm">
                              <Badge colorScheme={statusCol} size="sm" variant="subtle">
                                {t?.title || id.slice(-6)}
                              </Badge>
                              <Button
                                size="xs"
                                variant="ghost"
                                colorScheme="gray"
                                onClick={() => setDependsOn(dependsOn.filter((d) => d !== id))}
                                aria-label="Ukloni"
                              >
                                ×
                              </Button>
                            </HStack>
                          );
                        })}
                      </VStack>
                      <Text fontSize="xs" color="gray.500" mt={2}>
                        This task can only be done after the listed tasks are completed.
                      </Text>
                    </Box>
                  )}
                </VStack>
              </Dialog.Body>
              <Dialog.Footer flexShrink={0} borderTopWidth={1} borderColor="gray.100">
                <HStack justifyContent="flex-end">
                  <Button variant="ghost" onClick={onClose}>Cancel</Button>
                  <Button colorScheme="blue" onClick={handleSubmit} loading={loading}>Create</Button>
                </HStack>
              </Dialog.Footer>
            </Box>
          )}
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};

export default TaskModal;
